import { Link } from '@tanstack/react-router'
import { formatCurrency, formatPercentage, getStatusColor } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Star, TrendingUp, Users, Download, Shield, Clock } from 'lucide-react'
import { analytics } from '@/hooks/use-posthog'

interface AlgorithmCardProps {
  algorithm: any
  viewMode?: 'grid' | 'list'
}

export function AlgorithmCard({ algorithm, viewMode = 'grid' }: AlgorithmCardProps) {
  const isGridView = viewMode === 'grid'

  const handleClick = () => {
    analytics.algorithmViewed(algorithm.id, algorithm.name)
  }

  return (
    <Link to={`/algorithms/${algorithm.slug}`} onClick={handleClick}>
      <div className={`card hover:shadow-lg transition-all duration-200 cursor-pointer hover-lift ${!isGridView ? 'p-6 flex items-center justify-between' : 'p-6'
        }`}>
        {isGridView ? (
          /* Grid View */
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
                  {algorithm.name}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {algorithm.description}
                </p>
              </div>
              <div className="flex items-center space-x-1 ml-2">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-medium">
                  {algorithm.rating?.toFixed(1) || 'N/A'}
                </span>
                <span className="text-xs text-muted-foreground">
                  ({algorithm.reviewsCount || 0})
                </span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1">
              {algorithm.category?.name && (
                <span className="badge badge-primary text-xs">
                  {algorithm.category.name}
                </span>
              )}
              {algorithm.requirements?.propFirmCompatible && (
                <span className="badge badge-success text-xs">
                  <Shield className="h-3 w-3 mr-1" />
                  Prop Firm
                </span>
              )}
              {algorithm.featured && (
                <span className="badge badge-warning text-xs">
                  Featured
                </span>
              )}
            </div>

            {/* Performance */}
            {algorithm.performance && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Total Return</div>
                  <div className={`font-semibold ${algorithm.performance.totalReturn >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                    {formatPercentage(algorithm.performance.totalReturn)}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Win Rate</div>
                  <div className="font-semibold">
                    {algorithm.performance.winRate?.toFixed(1) || 'N/A'}%
                  </div>
                </div>
              </div>
            )}

            {/* Developer */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center">
                  <Users className="h-3 w-3 text-primary" />
                </div>
                <span className="text-muted-foreground">
                  {algorithm.developer?.name || 'Unknown Developer'}
                </span>
                {algorithm.developer?.verified && (
                  <Shield className="h-3 w-3 text-green-500" />
                )}
              </div>
            </div>

            {/* Price and Actions */}
            <div className="flex items-center justify-between">
              <div className="text-xl font-bold text-primary">
                {algorithm.price && algorithm.price > 0
                  ? formatCurrency(algorithm.price)
                  : 'Early Access'}
              </div>
              <div className="flex items-center space-x-1 text-muted-foreground">
                <Download className="h-4 w-4" />
                <span className="text-sm">{algorithm.downloads || 0}</span>
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="flex-1 grid grid-cols-12 gap-4 items-center">
            <div className="col-span-4">
              <div className="flex items-center space-x-3">
                <h3 className="font-semibold text-foreground">{algorithm.name}</h3>
                {algorithm.featured && (
                  <span className="badge badge-warning text-xs">Featured</span>
                )}
                {algorithm.requirements?.propFirmCompatible && (
                  <span className="badge badge-success text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    Prop Firm
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                {algorithm.description}
              </p>
              <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Users className="h-3 w-3" />
                  <span>{algorithm.developer?.name || 'Unknown'}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Download className="h-3 w-3" />
                  <span>{algorithm.downloads || 0}</span>
                </span>
              </div>
            </div>

            <div className="col-span-2">
              <div className="flex items-center space-x-2">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">
                  {algorithm.rating?.toFixed(1) || 'N/A'}
                </span>
                <span className="text-muted-foreground">
                  ({algorithm.reviewsCount || 0})
                </span>
              </div>
            </div>

            <div className="col-span-2">
              <div className={`font-semibold ${algorithm.performance?.totalReturn >= 0 ? 'text-green-500' : 'text-red-500'
                }`}>
                {algorithm.performance?.totalReturn
                  ? formatPercentage(algorithm.performance.totalReturn)
                  : 'N/A'
                }
              </div>
              <div className="text-sm text-muted-foreground">
                {algorithm.performance?.winRate?.toFixed(1) || 'N/A'}% win rate
              </div>
            </div>

            <div className="col-span-2">
              <div className="text-lg font-bold text-primary">
                {algorithm.price && algorithm.price > 0
                  ? formatCurrency(algorithm.price)
                  : 'Early Access'}
              </div>
            </div>

            <div className="col-span-2 text-right">
              <Button size="sm">
                View Details
              </Button>
            </div>
          </div>
        )}
      </div>
    </Link>
  )
}